import { assessmentTaskStatusPresentation, isAssessmentTaskStatus } from "../utils/assessment_task_status.util";
import type { AssessmentTaskPage, AssessmentTaskRequirementRef, AssessmentTaskStatus,
    AssessmentTaskSummary } from "./assessment_tasks.types";
import type { EvidenceRequirementStatus } from "./evidence_requirements.service";

export type AssessmentWorkItemKind = "task" | "evidence";
export type AssessmentWorkItemType = AssessmentWorkItemKind | "all";
export type AssessmentWorkItemView = "header" | "assessment_footer" | "requirement_footer";
export type AssessmentWorkItemStatusSelector =
    | `task:${AssessmentTaskStatus}`
    | `evidence:${EvidenceRequirementStatus}`;

export interface AssessmentWorkItemEvidenceAssignee {
    userId: number;
    displayName: string;
    assignedAt?: string | null;
    assignedBy?: { userId: number; displayName: string } | null;
}

export interface AssessmentWorkItemEvidenceCapabilities {
    canUpload: boolean;
    canEditEvidence: boolean;
    canEditEvidenceDetails: boolean;
    canDeleteEvidence: boolean;
    canEmailEvidence: boolean;
}

// This is a safe read projection, not a PersistedEvidenceRequirement write DTO.
export interface AssessmentWorkItemEvidenceSummary {
    id: number;
    productLicenseId: number;
    merchantId: number;
    assessmentLabel: string;
    merchantLabel: string;
    evidenceName: string;
    status: EvidenceRequirementStatus;
    dueDate: string | null;
    assignees: AssessmentWorkItemEvidenceAssignee[];
    requirements: AssessmentTaskRequirementRef[];
    createdAt: string | null;
    updatedAt: string | null;
    capabilities: AssessmentWorkItemEvidenceCapabilities;
    linkedSecureStorageFileId: number | null;
    linkedFileName: string | null;
    linkedFileAccessible: boolean;
}

export interface AssessmentWorkItemEvidenceDetail extends AssessmentWorkItemEvidenceSummary {
    linkedSecureStorageFileId: number | null;
    linkedFileName: string | null;
    linkedFileAccessible: boolean;
}

export type AssessmentWorkItem =
    | { kind: "task"; task: AssessmentTaskSummary; evidence?: never }
    | { kind: "evidence"; evidence: AssessmentWorkItemEvidenceSummary; task?: never };

export type AssessmentWorkItemPage = AssessmentTaskPage<AssessmentWorkItem>;

export interface AssessmentWorkItemOffsetPage<T> {
    items: T[];
    offset: number;
    limit: number;
    total: number;
}

export interface AssessmentWorkItemAssessmentOption {
    productLicenseId: number;
    label: string;
}

export interface AssessmentWorkItemAssigneeOption {
    userId: number;
    label: string;
}

export interface AssessmentWorkItemStatusOption {
    value: AssessmentWorkItemStatusSelector;
    label: string;
}

export interface AssessmentWorkItemCount {
    count: number;
}

export interface AssessmentWorkItemListFilters {
    page?: number;
    pageSize?: number;
    search?: string;
    assignedToMe?: boolean;
    assigneeUserIds?: readonly number[];
    assessmentIds?: readonly number[];
    statuses?: readonly AssessmentWorkItemStatusSelector[];
    itemType?: AssessmentWorkItemType;
    view?: AssessmentWorkItemView;
    requirement?: AssessmentTaskRequirementRef;
}

export interface AssessmentWorkItemOptionFilters
    extends Omit<AssessmentWorkItemListFilters, "page" | "pageSize" | "search"> {
    offset?: number;
    limit?: number;
    // Option-label search is separate from the work-item content search.
    search?: string;
    itemSearch?: string;
}

export interface AssessmentWorkItemStatusPresentation {
    label: string;
    tagColor: "warning" | "processing" | "error" | "success";
}

const EVIDENCE_STATUS_PRESENTATION: Readonly<Record<EvidenceRequirementStatus,
    AssessmentWorkItemStatusPresentation>> = {
    not_submitted: { label: "Not Submitted", tagColor: "warning" },
    file_pending_review: { label: "File Pending Review", tagColor: "processing" },
    file_rejected: { label: "File Rejected", tagColor: "error" },
    submitted_reviewed: { label: "Submitted & Reviewed", tagColor: "success" }
};

export function isEvidenceWorkItemStatus(value: unknown): value is EvidenceRequirementStatus {
    return typeof value === "string" &&
        Object.prototype.hasOwnProperty.call(EVIDENCE_STATUS_PRESENTATION, value);
}

function assertValidWorkItem(item: AssessmentWorkItem): void {
    if (!item || (item.kind !== "task" && item.kind !== "evidence")) {
        throw new Error("Unknown work-item kind.");
    }

    const source = item.kind === "task" ? item.task : item.evidence;
    if (!source || !Number.isSafeInteger(source.id) || source.id <= 0) {
        throw new Error("A valid source-qualified work-item ID is required.");
    }

    const hasKnownStatus = item.kind === "task"
        ? isAssessmentTaskStatus(item.task.status)
        : isEvidenceWorkItemStatus(item.evidence.status);
    if (!hasKnownStatus) {
        throw new Error("Unknown work-item status.");
    }
}

export function workItemIdentity(item: AssessmentWorkItem): string {
    assertValidWorkItem(item);
    const sourceId = item.kind === "task" ? item.task.id : item.evidence.id;
    return `${item.kind}:${sourceId}`;
}

export function workItemStatusSelector(item: AssessmentWorkItem): AssessmentWorkItemStatusSelector {
    assertValidWorkItem(item);
    return item.kind === "task" ? `task:${item.task.status}` : `evidence:${item.evidence.status}`;
}

export function workItemStatusPresentation(item: AssessmentWorkItem): AssessmentWorkItemStatusPresentation {
    assertValidWorkItem(item);
    return item.kind === "task"
        ? assessmentTaskStatusPresentation(item.task.status)
        : EVIDENCE_STATUS_PRESENTATION[item.evidence.status];
}

export function isFinishedWorkItem(item: AssessmentWorkItem): boolean {
    assertValidWorkItem(item);
    return item.kind === "task"
        ? item.task.status === "approved"
        : item.evidence.status === "submitted_reviewed";
}
